import React, { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { Download, Users, Receipt, Send, Loader2, FileSpreadsheet } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function AdminExports() {
  const { t } = useLanguage();
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingTx, setLoadingTx] = useState(false);
  const [loadingTr, setLoadingTr] = useState(false);
  const [txRange, setTxRange] = useState({ from: '', to: '' });
  const [trRange, setTrRange] = useState({ from: '', to: '' });

  const downloadClients = async () => {
    try {
      setLoadingClients(true);
      const r = await api.get('/admin/export/clients', { responseType: 'blob' });
      triggerBlobDownload(r.data, `clients_export_${todayIso()}.csv`);
      toast.success('Export clients téléchargé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'export");
    } finally {
      setLoadingClients(false);
    }
  };

  const downloadTransactions = async () => {
    try {
      setLoadingTx(true);
      const params = {};
      if (txRange.from) params.from = txRange.from;
      if (txRange.to) params.to = txRange.to;
      const r = await api.get('/admin/export/transactions', { params, responseType: 'blob' });
      const suffix = txRange.from || txRange.to ? `_${txRange.from || 'debut'}_to_${txRange.to || 'fin'}` : '';
      triggerBlobDownload(r.data, `transactions_export${suffix}_${todayIso()}.csv`);
      toast.success('Export transactions téléchargé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'export");
    } finally {
      setLoadingTx(false);
    }
  };

  const downloadTransfers = async () => {
    try {
      setLoadingTr(true);
      const params = {};
      if (trRange.from) params.from = trRange.from;
      if (trRange.to) params.to = trRange.to;
      const r = await api.get('/admin/export/transfers', { params, responseType: 'blob' });
      const suffix = trRange.from || trRange.to ? `_${trRange.from || 'debut'}_to_${trRange.to || 'fin'}` : '';
      triggerBlobDownload(r.data, `virements_export${suffix}_${todayIso()}.csv`);
      toast.success('Export virements téléchargé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'export");
    } finally {
      setLoadingTr(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
          <FileSpreadsheet className="w-7 h-7 text-blue-700 dark:text-blue-400" /> {t('admin.exportsTitle')}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {t('admin.exportsSubtitle')}
        </p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {/* Clients export */}
        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Users className="w-5 h-5 text-blue-700 dark:text-blue-400" /> Clients
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              Liste complète des clients (informations, solde, statut, dates).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Le fichier sera nommé <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">clients_export_{todayIso()}.csv</code>.
            </p>
            <Button
              onClick={downloadClients}
              disabled={loadingClients}
              className="w-full bg-blue-700 hover:bg-blue-800"
              data-testid="btn-export-clients"
            >
              {loadingClients ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Télécharger CSV
            </Button>
          </CardContent>
        </Card>

        {/* Transactions export */}
        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Receipt className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Transactions
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              Toutes les transactions sur une période donnée.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs dark:text-slate-300">Du</Label>
                <Input
                  type="date"
                  value={txRange.from}
                  onChange={(e) => setTxRange({ ...txRange, from: e.target.value })}
                  data-testid="input-export-tx-from"
                />
              </div>
              <div>
                <Label className="text-xs dark:text-slate-300">Au</Label>
                <Input
                  type="date"
                  value={txRange.to}
                  onChange={(e) => setTxRange({ ...txRange, to: e.target.value })}
                  data-testid="input-export-tx-to"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Laissez vide pour exporter l'historique complet.
            </p>
            <Button
              onClick={downloadTransactions}
              disabled={loadingTx}
              className="w-full bg-blue-700 hover:bg-blue-800"
              data-testid="btn-export-transactions"
            >
              {loadingTx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Télécharger CSV
            </Button>
          </CardContent>
        </Card>

        {/* Transfers export */}
        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-white">
              <Send className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Virements
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              Tous les virements SEPA et SWIFT sur une période donnée.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs dark:text-slate-300">Du</Label>
                <Input
                  type="date"
                  value={trRange.from}
                  onChange={(e) => setTrRange({ ...trRange, from: e.target.value })}
                  data-testid="input-export-tr-from"
                />
              </div>
              <div>
                <Label className="text-xs dark:text-slate-300">Au</Label>
                <Input
                  type="date"
                  value={trRange.to}
                  onChange={(e) => setTrRange({ ...trRange, to: e.target.value })}
                  data-testid="input-export-tr-to"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Laissez vide pour exporter l'historique complet.
            </p>
            <Button
              onClick={downloadTransfers}
              disabled={loadingTr}
              className="w-full bg-blue-700 hover:bg-blue-800"
              data-testid="btn-export-transfers"
            >
              {loadingTr ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Télécharger CSV
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
